import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { extractCursor } from '@/api/util'

export let perPageItemNum = 20

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  image: {
    images: [],
    nextCursor: ''
  },
  importantImage: {
    images: [],
    nextCursor: ''
  },
  patientUploadImage: {
    name: '',
    base64: ''
  },
  isInAppBrowser: false
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  images: state => state.image.images,
  hasImageNext: state => !!state.image.nextCursor,
  importantImages: state => {
    return state.importantImage.images.sort((a, b) => b.id - a.id)
  },
  hasImportantImageNext: state => !!state.importantImage.nextCursor,
  patientUploadImage: state => state.patientUploadImage,
  isInAppBrowser: state => state.isInAppBrowser
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  updateImages: (state, images) => {
    state.image.images = images
  },
  addImages: (state, images) => {
    state.image.images = [...state.image.images, ...images]
  },
  updateImageNext: (state, next) => {
    state.image.nextCursor = extractCursor(next)
  },
  updateImportantImages: (state, images) => {
    state.importantImage.images = images
  },
  addImportantImages: (state, images) => {
    state.importantImage.images = [...state.importantImage.images, ...images]
  },
  updateImportantImageNext: (state, next) => {
    state.importantImage.nextCursor = extractCursor(next)
  },
  updateImportantStatus: (state, { isImportant, imageId }) => {
    const importantImages = state.importantImage.images
    const ids = importantImages.map(image => image.id)
    const image = state.image.images.find(im => im.id === imageId)
    const youngestId = ids.sort((a, b) => a - b)[0]
    const next = !!state.importantImage.nextCursor
    if (image) {
      const index = state.image.images.indexOf(image)
      image.isImportant = isImportant
      state.image.images.splice(index, 1, image)
    }
    if (
      isImportant &&
      (!next ||
        imageId > youngestId ||
        importantImages.length < perPageItemNum) &&
      !ids.includes(imageId)
    ) {
      const youngerId = ids.find(id => id < imageId) || 0
      if (image)
        state.importantImage.images.splice(ids.indexOf(youngerId) - 1, 0, image)
    }
    if (!isImportant) {
      state.importantImage.images = importantImages.filter(
        image => image.id !== imageId
      )
    }
  },
  removeImage: (state, { imageId }) => {
    state.image.images = state.image.images.filter(
      image => image.id !== imageId
    )
    state.importantImage.images = state.importantImage.images.filter(
      image => image.id !== imageId
    )
  },
  updatePatientUploadImage: (state, { name, base64 }) => {
    state.patientUploadImage = { name, base64 }
  },
  setIsInAppBrowser: (state, inapp) => {
    state.isInAppBrowser = inapp
  }
}
/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  updateImages: async ({ commit }, { patientId }) => {
    const res = await Vue.$api.image.fetch({ patientId })
    perPageItemNum = res.results.length || perPageItemNum
    commit('updateImages', res.results)
    commit('updateImageNext', res.next || '')
  },
  loadNextImages: async ({ commit, state }, { patientId }) => {
    const cursor = state.image.nextCursor
    if (!cursor) return
    const res = await Vue.$api.image.fetch({ patientId, cursor })
    commit('addImages', res.results)
    commit('updateImageNext', res.next || '')
  },
  updateImportantImages: async ({ commit }, { patientId }) => {
    const res = await Vue.$api.image.fetch({ patientId, isImportant: true })
    commit('updateImportantImages', res.results)
    commit('updateImportantImageNext', res.next || '')
  },
  loadNextImportantImages: async ({ commit, state }, { patientId }) => {
    const cursor = state.importantImage.nextCursor
    if (!cursor) return
    const res = await Vue.$api.image.fetch({
      patientId,
      isImportant: true,
      cursor
    })
    commit('addImportantImages', res.results)
    commit('updateImportantImageNext', res.next || '')
  },
  updateImportantStatus: async (
    { commit },
    { patientId, isImportant, imageId }
  ) => {
    await Vue.$api.image.updateImportant({
      patientId,
      isImportant,
      imageId
    })
    commit('updateImportantStatus', { isImportant, imageId })
  },
  removeImage: async ({ commit }, { patientId, imageId }) => {
    await Vue.$api.image.delete({ patientId, imageId })
    commit('removeImage', { imageId })
  },
  updatePatientUploadImage: ({ commit }, { name, base64 }) => {
    commit('updatePatientUploadImage', { name, base64 })
  },
  setIsInAppBrowser: ({ commit }, inapp) => {
    commit('setIsInAppBrowser', inapp)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
